import { useEffect, useState } from 'react'
import axios from 'axios'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { window } from "browser-monads"

export const apiUrl = 'https://api.vibemap.com/v0.3'


// Get all lists
export const getAllLists = async (partner_slug = null) => {
    try {
        const cacheBuster = new Date().getTime()
        const endpoint = `${apiUrl}/lists/?_=${cacheBuster}${partner_slug ? `&organization=${partner_slug}` : ''}`
        const response = await axios.get(endpoint)

        if (response.status !== 200) {
            throw new Error(`Error fetching lists: ${response.statusText}`)
        }
        if (!response.data) {
            throw new Error('No data received')
        }

        const data = response?.data?.results || []

        return data
    } catch (error) {
        console.error('ERROR: getAllLists', error)
        throw error
    }
}


// Get lists for a partner
export const getPartnerLists = async (email) => {
    try {
        const endpoint = `${apiUrl}/partners/`
        const response = await axios.get(endpoint)
        return response.data
    } catch (error) {
        console.error('ERROR: getPartnerLists', error)
        throw error
    }
}


// Get lists for a user
// TODO: This will be used with TanStack Query in the future
export const getUserLists = async (userId) => {
    try {
        const cacheBuster = new Date().getTime()
        const endpoint = `${apiUrl}/lists/?user_id=${userId}&_=${cacheBuster}`
        const response = await axios.get(endpoint)

        if (response.status !== 200) {
            throw new Error(`Error fetching user lists: ${response.statusText}`)
        }

        const data = response?.data?.results || []
        return data
    } catch (error) {
        console.error('ERROR: getUserLists', error)
        throw error
    }
}


// Fetch a list by ID
export const getList = async (listId) => {
    try {
        const endpoint = `${apiUrl}/lists/${listId}/`
        const response = await axios.get(endpoint, {
            headers: { 'Content-Type': 'application/json' }
        })
        return response.data
    } catch (error) {
        console.error('ERROR: fetchList', error)
        throw error
    }
}


// Get details for a specific partner
export const getPartnerDetails = async (slug) => {
    try {
        const endpoint = `${apiUrl}/partners/${slug}/`
        const response = await axios.get(endpoint)
        return response.data
    } catch (error) {
        console.error('ERROR: getPartnerDetails', error)
        return null
    }
}


// Create a new list
export const createList = async (listName, setAllLists, setMyLists) => {
    try {
        const endpoint = `${apiUrl}/lists/`
        const response = await axios.post(endpoint, { name: listName })
        const newList = response.data
        return newList
    } catch (err) {
        throw err
    }
}

export const deleteList = async (listId, setAllLists, setMyLists) => {
    try {
        const endpoint = `${apiUrl}/lists/${listId}/`
        const response = await axios.delete(endpoint)
        if (response.status !== 204) {
            throw new Error(`Error deleting list: ${response.statusText}`)
        }
    } catch (err) {
        console.error('ERROR: deleteList', err)
        throw err
    }
}

// Save changes to an existing list
export const saveList = async (listId, setAllLists, setMyLists) => {
    try {
        const endpoint = `${apiUrl}/lists/${listId}/`
        const response = await axios.patch(endpoint, { name: listId })
        const updatedList = response.data
        setAllLists((prevLists) =>
            prevLists.map((list) => (list.id === updatedList.id ? updatedList : list))
        )
        setMyLists((prevLists) =>
            prevLists.map((list) => (list.id === updatedList.id ? updatedList : list))
        )
        return updatedList
    } catch (err) {
        throw err
    }
}


// Extract search parameters from a list
export const getListSearchParams = (list) => {
    if (!list || !list.search_params) return {}

    // Remove any leading '?' from searchQuery to avoid double question marks
    const cleanSearch = list.search_params?.startsWith('?')
        ? list.search_params.slice(1)
        : list.search_params

    return searchQueryToParams(cleanSearch)
}


// Set URL location based on list parameters
export const setListLocation = (list) => {
    if (!list) return

    const searchQuery = list?.search_params
    // Remove any leading '?' from searchQuery to avoid double question marks
    const cleanSearch = searchQuery?.startsWith('?') ? searchQuery.slice(1) : searchQuery

    // Start with the current pathname
    const url = new URL(window?.location?.origin + window?.location?.pathname + (cleanSearch ? '?' + cleanSearch : ''))

    // Add the list slug if available
    if (list?.slug) {
        url.searchParams.set('list', list.slug)
    }

    // Avoid double question marks
    const newURL = url.toString()
    window?.history.pushState({ path: newURL }, '', newURL)
    window?.location.reload()
}


// Check if search parameters match between current URL and a list
export const listsParamsMatch = (searchCur, searchList) => {
    const searchParams = new URLSearchParams(searchCur)
    const listParams = new URLSearchParams(searchList)

    let mismatches = []
    const isMatch = [...searchParams].every(([key, value]) => {
        if (key === 'list') return true // Ignore 'list' param
        const listValue = listParams.get(key)
        if (listValue !== value) {
            mismatches.push({ key, searchValue: value, listValue })
            return false
        }
        return true
    })

    //if (!isMatch) console.log('DEBUG: ListOptions params mismatch:', mismatches)
    return isMatch
}


// Convert search query string to an object, excluding 'list' param
export const searchQueryToParams = (searchQuery) => {
    const params = new URLSearchParams(searchQuery)
    const paramsObj = {}
    for (const [key, value] of params.entries()) {
        if (key === 'list') continue // Skip 'list' param
        if (value) {
            paramsObj[key] = value
        }
    }
    return paramsObj
}


// Helper to generate query params based on current state and theme
export const getQueryParams = (pageStore, mapStore) => {
  const { embedded } = pageStore
  const { clipToBounds } = mapStore

  // Always include embedded, and clipToBounds if needed
  const path_cur = window?.location?.pathname;
  const params = {
    embedded: embedded ? 1 : 1, // Always include embedded as 1, unless parameter is explicitly set to 0
    clipToBounds: clipToBounds ? 1 : 0,
    referrer: path_cur,
  }

  // Create query string from parameters object
  const searchPath = Object.entries(params).map(([paramName, paramValue]) => {
    const encodedValue = encodeURIComponent(paramValue)
    return `${paramName}=${encodedValue}`
  }).join('&')

  // Convert to query string
  return searchPath
}


// Update nav item parameters based on slug and query string
export const updateNavItemParams = (navItems, slug, queryString) => {
  return navItems.map(item => {
    if (item.slug === slug) {
      const path_base =  item.path.split('?')[0]
      // Remove leading '?' if present in queryString
      const cleanQueryString = queryString.startsWith('?') ? queryString.slice(1) : queryString;
      const path_new = `${path_base}?${cleanQueryString}`

      return { ...item, path: path_new }
    }

    return item
  })
}


// Update product navigation items with list parameters
export const updateNavItemsWithList = (navItems, list, themeName) => {
    if (!list || !navItems) return navItems

    return navItems.map(item => {
        // Skip items that don't need list parameters
        if (!item.path || !item.slug.includes('directory')) {
            return item
        }

        // Get the base path without query parameters
        const basePath = item.path.split('?')[0]

        // Get list search parameters
        const listParams = getListSearchParams(list)

        // Add theme if provided
        if (themeName) {
            listParams.theme = themeName
        }

        // Generate new path with parameters
        const queryString = paramsToSearchQuery(listParams)
        item.path = `${basePath}${queryString}`

        return item
    })
}


// Update navitems with city, theme, and primary list parameters
export const updateNavItemsWithCityAndTheme = (navItems, citySlug, themeName, listPrimary) => {
    const themeArg = `theme=${themeName}`
    const updatedItems = navItems.map(item => {
        // If there's a city slug, update the path to include it
        if (citySlug) {
            if (item.path && item.path.includes('cities=')) {
                item.path = item.path.replace(/cities=[^&]*/, `cities=${citySlug}`)
            }
            else if (item.path) {
                item.path = item.path + `&cities=${citySlug}`
            }
        }

        // If  main directory settings, apply those too.
        if (listPrimary && item.slug.includes('directory')) {
        // Combine the primary list params with the theme params
        const listParams = searchQueryToParams(listPrimary?.search_params) || {}
        const updatedParams = {
            ...listParams,
            theme: themeName,
            cities: citySlug
        }
        const base_path = item.path.split('?')[0]
        // Update the path with the new params
        item.path = base_path + paramsToSearchQuery(updatedParams)
        }

        if (item.path && item.path.includes('theme=')) {
            item.path = item.path.replace(/theme=[^&]*/, themeArg)
            } else if (item.path) {
            item.path = item.path + `&${themeArg}`
        }
        return item
    })
    return updatedItems
}


// Convert an object to a search query string, excluding 'list' param
export const paramsToSearchQuery = (paramsObj) => {
    const params = new URLSearchParams()
    for (const [key, value] of Object.entries(paramsObj)) {
        if (key === 'list') continue // Skip 'list' param
        if (value) {
            params.set(key, value)
        }
    }
    return params.toString() ? `?${params.toString()}` : ''
}


// Collect all exported async functions except the hook itself
const exportedAsyncFns = {
    getPartnerLists,
    getUserLists,
    getPartnerDetails,
    createList,
    deleteList,
    saveList,
    setListLocation,
    updateNavItemsWithList,
    listsParamsMatch,
    getList,
    getAllLists,
    searchQueryToParams,
    paramsToSearchQuery,
    getListSearchParams
}

// Define query keys
export const queryKeys = {
    lists: ['lists'],
    createList: ['createList'],
    getUserLists: ['getUserLists'],
    getPartnerLists: ['getPartnerLists'],
    getPartnerDetails: ['getPartnerDetails'],
    getList: ['getList'],
    saveList: ['saveList'],
}

// --- Hook using the above methods and providing loading/error state ---
const useListUtils = ({ shouldLoad = true, partner_slug = null, email = null} = {}) => {
    // Local state for loading and error handling
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)

    const isBrowser = typeof window !== 'undefined'
    // Only try to get the queryClient if we're in the browser
    let queryClient = null;
    try {
        queryClient = isBrowser ? useQueryClient() : null;
    } catch (e) {
        console.warn("QueryClient not available, some features will be disabled");
    }

    // Custom hook for getting all lists using TanStack Query
    const useAllLists = (options = {}) => {
        try {
            return useQuery({
                queryKey: queryKeys.lists,
                queryFn: async () => {
                    const data = await getAllLists(partner_slug)
                    return data
                },
                ...options
            })
        } catch (error) {
            console.error('Error in useAllLists:', error)
            return {
                data: [],
                isLoading: false,
                error,
            }
        }
    }

    // Create list using TanStack Query
    const useCreateList = () => {
        if (!queryClient) {
            return {
                mutate: () => console.warn("Cannot create list during SSR"),
                isPending: false,
                error: null
            }
        }

        return useMutation({
            mutationFn: createList,
            onSuccess: (newList) => {
                // Invalidate lists query to trigger a refetch
                queryClient.invalidateQueries({ queryKey: queryKeys.lists })
                debugger
                // Optionally update the cache directly for immediate UI updates
                queryClient.setQueryData(queryKeys.lists, (oldData) => {
                    return oldData ? [...oldData, newList] : [newList]
                })
            }
        })
    }

    // Delete list using TanStack Query
    const useDeleteList = () => {
        if (!queryClient) {
            return {
                mutate: () => console.warn("Cannot create list during SSR"),
                isPending: false,
                error: null
            }
        }

        return useMutation({
            mutationFn: deleteList,
            onSuccess: (deletedListId) => {
                // Invalidate lists query to trigger a refetch
                queryClient.invalidateQueries({ queryKey: queryKeys.lists })
                // Optionally update the cache directly for immediate UI updates
                queryClient.setQueryData(queryKeys.lists, (oldData) => {
                    return oldData ? oldData.filter(list => list.id !== deletedListId) : []
                })
            }
        })
    }

    // Use TanStack Query to fetch all lists if shouldLoad is true
    const { data: allLists, isLoading: isLoadingLists, error: listsError } = useAllLists({
        enabled: shouldLoad,
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60 * 5, // 5 minutes
        retry: 1,
    })

    // Get the mutation functions
    const { mutate: createListMutation, isPending: isCreatingList, error: createListError } = useCreateList()
    const { mutate: deleteListMutation } = useDeleteList()

    const wrappedFns = {}
    Object.entries(exportedAsyncFns).forEach(([fnName, fn]) => {
        if (typeof fn === 'function') {
            wrappedFns[fnName] = (...args) => {
                try {
                    const result = fn(...args)
                    // If result is a Promise, handle async
                    if (result && typeof result.then === 'function') {
                        return result
                            .then((res) => res)
                            .catch((err) => {
                                setError(err)
                            })
                    } else {
                        // Sync function
                        return result
                    }
                } catch (err) {
                    setError(err)
                }
            }
        }
    })

    return {
        ...wrappedFns,
        allLists,
        createList: createListMutation,
        isCreatingList,
        createListError,
        loading: isLoadingLists,
        error: listsError || createListError,
    }
}

export default useListUtils